import { type Theme } from '@app-types/theme';
import { useLocalStorage } from '@composables/useLocalStorage';
import { THEMES } from '@utils/theme';
import { onMounted, onUnmounted, watch } from 'vue';

const THEME_STORAGE_KEY = 'theme';
const DATA_THEME_ATTR = 'data-theme';
const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';

export function useTheme() {
  const theme = useLocalStorage<Theme>(THEME_STORAGE_KEY, THEMES.DEFAULT, {
    serializer: {
      read: (v: string) => {
        if (v === THEMES.DARK || v === THEMES.LIGHT) return v as Theme;
        return THEMES.DEFAULT;
      },
      write: (v: Theme) => String(v),
    },
  });

  const applyTheme = (newTheme: Theme) => {
    if (newTheme === THEMES.DEFAULT) {
      if (window.matchMedia(DARK_MODE_QUERY).matches) {
        document.body.setAttribute(DATA_THEME_ATTR, THEMES.DARK);
      } else {
        document.body.setAttribute(DATA_THEME_ATTR, THEMES.LIGHT);
      }
    } else {
      document.body.setAttribute(DATA_THEME_ATTR, newTheme);
    }
  };

  const handleSystemThemeChange = (_: MediaQueryListEvent) => {
    if (theme.value === THEMES.DEFAULT) {
      applyTheme(THEMES.DEFAULT);
    }
  };

  onMounted(() => {
    applyTheme(theme.value);
    window.matchMedia(DARK_MODE_QUERY).addEventListener('change', handleSystemThemeChange);
  });

  onUnmounted(() => {
    window.matchMedia(DARK_MODE_QUERY).removeEventListener('change', handleSystemThemeChange);
  });

  watch(theme, newTheme => {
    applyTheme(newTheme);
  });

  const toggleTheme = () => {
    theme.value = theme.value === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
  };

  const setTheme = (newTheme: Theme) => {
    theme.value = newTheme;
  };

  return {
    setTheme,
    theme,
    toggleTheme,
  };
}
